import type { TokenPairDto } from "../../shared/contracts/model/tokenPairDto";
import type { LayeredStorage } from "./storage";

export type TokensListener = (tokens: TokenPairDto | null) => void;

export class TokenStore {
  private tokens: TokenPairDto | null = null;
  private loadPromise: Promise<TokenPairDto | null> | null = null;
  private mutationRevision = 0;
  private persistenceQueue: Promise<void> = Promise.resolve();
  private readonly listeners = new Set<TokensListener>();

  constructor(
    private readonly storage: LayeredStorage,
    private readonly accessKey: string,
    private readonly refreshKey: string,
  ) {}

  async load(): Promise<TokenPairDto | null> {
    if (this.tokens) {
      return this.tokens;
    }

    if (!this.loadPromise) {
      const loadRevision = this.mutationRevision;
      const pendingPersistence = this.persistenceQueue;
      this.loadPromise = (async () => {
        await pendingPersistence;
        if (this.mutationRevision !== loadRevision) {
          return this.tokens;
        }

        const [accessTokenRaw, refreshTokenRaw] = await Promise.all([
          this.storage.getItem(this.accessKey),
          this.storage.getItem(this.refreshKey),
        ]);

        if (this.mutationRevision !== loadRevision) {
          return this.tokens;
        }

        const accessToken =
          typeof accessTokenRaw === "string" ? accessTokenRaw : undefined;
        const refreshToken =
          typeof refreshTokenRaw === "string" ? refreshTokenRaw : undefined;

        if (accessToken && refreshToken) {
          this.tokens = { accessToken, refreshToken };
        } else {
          this.tokens = null;
        }
        return this.tokens;
      })().finally(() => {
        this.loadPromise = null;
      });
    }

    return this.loadPromise;
  }

  getTokens(): TokenPairDto | null {
    return this.tokens;
  }

  getAccessToken(): string | null {
    return this.tokens?.accessToken ?? null;
  }

  getRefreshToken(): string | null {
    return this.tokens?.refreshToken ?? null;
  }

  async setTokens(next: TokenPairDto | null): Promise<void> {
    const revision = ++this.mutationRevision;
    this.tokens =
      next &&
      typeof next.accessToken === "string" &&
      typeof next.refreshToken === "string"
        ? next
        : null;
    const tokensToPersist = this.tokens;
    const persist = async (): Promise<void> => {
      if (revision !== this.mutationRevision) return;

      if (tokensToPersist) {
        await Promise.all([
          this.storage.setItem(this.accessKey, tokensToPersist.accessToken),
          this.storage.setItem(this.refreshKey, tokensToPersist.refreshToken),
        ]);
      } else {
        await Promise.all([
          this.storage.removeItem(this.accessKey),
          this.storage.removeItem(this.refreshKey),
        ]);
      }
    };
    const persistence = this.persistenceQueue.then(persist, persist);
    this.persistenceQueue = persistence.catch(() => undefined);
    await persistence;

    if (revision === this.mutationRevision) {
      this.notify();
    }
  }

  subscribe(listener: TokensListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify() {
    for (const listener of this.listeners) {
      listener(this.tokens);
    }
  }
}
